import React from 'react';
import styled from 'styled-components';
import { alto, apple, bondiBlue, deepCerulean, dustGray, emperor, hotCinnamon, java, limeade, mineShaft, oregon, thunderBird, totemPole, veniceBlue, white } from '../Colors/Colors';
import { FocusRing } from '@react-aria/focus';
export default function Button(_ref) {
  let {
    children = 'Done',
    group = 'default',
    isPipeSplit = false,
    isTransparent = false,
    type = 'button',
    disabled = false,
    size = 'medium',
    ghost = false,
    block = false,
    width,
    noBorder = false,
    context = 'default',
    onClick,
    ...props
  } = _ref;

  const handleClick = e => {
    if (disabled) return;
    onClick && onClick(e);
  };

  const getClassNames = () => {
    const classNames = props.className ? props.className.split(' ') : [];
    size ? classNames.push(size) : classNames.push('medium');

    if (disabled) {
      classNames.push('disabled');
    }

    return classNames.join(' ');
  };

  const renderChildren = () => {
    const array = React.Children.toArray(children);
    const pipe = /*#__PURE__*/React.createElement(Pipe, {
      id: "pipe",
      size: size,
      context: context,
      ghost: ghost
    });
    return /*#__PURE__*/React.createElement(React.Fragment, null, array[0], isPipeSplit && pipe, array[1]);
  };

  const buttonProps = { ...props,
    group,
    ghost,
    isTransparent,
    context: context,
    block,
    width,
    noBorder,
    ariaDisabled: disabled,
    className: getClassNames(),
    onClick: handleClick,
    onMouseLeave: e => e.currentTarget.blur(),
    type: type || 'button',
    childrenLength: React.Children.toArray(children).length
  };
  return /*#__PURE__*/React.createElement(FocusRing, {
    focusRingClass: "focus-ring"
  }, /*#__PURE__*/React.createElement(StyledButton, buttonProps, /*#__PURE__*/React.createElement(ButtonContent, null, renderChildren())));
}

const getColor = function () {
  var _theme$whiteSpace, _theme$whiteSpace2, _theme$whiteSpace3, _theme$whiteSpace4, _theme$whiteSpace5, _theme$whiteSpace6, _theme$whiteSpace7;

  let context = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'default';
  let isGhost = arguments.length > 1 ? arguments[1] : undefined;
  let theme = arguments.length > 2 ? arguments[2] : undefined;
  let key = arguments.length > 3 ? arguments[3] : undefined;
  let isTransparent = arguments.length > 4 ? arguments[4] : undefined;
  if (isGhost && key === 'mainColor') return (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace = theme.whiteSpace) === null || _theme$whiteSpace === void 0 ? void 0 : _theme$whiteSpace.base) || white;
  const buttonContexts = {
    default: {
      fontColor: isGhost || isTransparent ? java : white,
      mainColor: isGhost ? (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace2 = theme.whiteSpace) === null || _theme$whiteSpace2 === void 0 ? void 0 : _theme$whiteSpace2.base) || white : isTransparent ? 'transparent' : java,
      shadeColor: bondiBlue
    },
    configure: {
      fontColor: isGhost || isTransparent ? deepCerulean : white,
      mainColor: isGhost ? (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace3 = theme.whiteSpace) === null || _theme$whiteSpace3 === void 0 ? void 0 : _theme$whiteSpace3.base) || white : isTransparent ? 'transparent' : deepCerulean,
      shadeColor: veniceBlue
    },
    dark: {
      fontColor: isGhost || isTransparent ? emperor : white,
      mainColor: isGhost ? (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace4 = theme.whiteSpace) === null || _theme$whiteSpace4 === void 0 ? void 0 : _theme$whiteSpace4.base) || white : isTransparent ? 'transparent' : emperor,
      shadeColor: mineShaft
    },
    error: {
      fontColor: isGhost || isTransparent ? thunderBird : white,
      mainColor: isGhost ? (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace5 = theme.whiteSpace) === null || _theme$whiteSpace5 === void 0 ? void 0 : _theme$whiteSpace5.base) || white : isTransparent ? 'transparent' : thunderBird,
      shadeColor: totemPole
    },
    primary: {
      fontColor: isGhost || isTransparent ? apple : white,
      mainColor: isGhost ? (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace6 = theme.whiteSpace) === null || _theme$whiteSpace6 === void 0 ? void 0 : _theme$whiteSpace6.base) || white : isTransparent ? 'transparent' : apple,
      shadeColor: limeade
    },
    warning: {
      fontColor: isGhost || isTransparent ? hotCinnamon : white,
      mainColor: isGhost ? (theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace7 = theme.whiteSpace) === null || _theme$whiteSpace7 === void 0 ? void 0 : _theme$whiteSpace7.base) || white : isTransparent ? 'transparent' : hotCinnamon,
      shadeColor: oregon
    }
  };
  return buttonContexts[context || 'default'][key];
};

const StyledButton = /*#__PURE__*/styled.button.attrs(props => ({
  'aria-disabled': props.ariaDisabled
})).withConfig({
  displayName: "Button__StyledButton",
  componentId: "sc-1jofteb-0"
})(["box-sizing:border-box;display:flex;justify-content:center;align-items:center;text-decoration:none;text-transform:capitalize;background-color:", ";border:", ";font-weight:600;white-space:nowrap;text-overflow:ellipsis;overflow:hidden;cursor:pointer;svg{top:0;margin-right:", ";}&,&:link,&:hover,&:visited{color:", ";}&:hover,&.focus-ring{background:", ";color:", ";text-decoration:none;#pipe{border-right-color:", ";}}&:active{background:", ";text-decoration:none;}&.disabled,&.focus-ring.disabled{color:", ";background-color:", ";cursor:not-allowed;border:none;}&.xsmall{width:", ";height:", ";border-radius:", ";padding:", ";font-size:13px;line-height:", ";}&.small{width:", ";height:", ";border-radius:", ";padding:", ";font-size:14px;line-height:", ";}&.medium{width:", ";height:", ";border-radius:", ";padding:", ";font-size:16px;line-height:", ";}&.large{width:", ";height:", ";border-radius:", ";padding:", ";font-size:20px;line-height:", ";}&.focus-ring{outline:2px solid ", ";outline-offset:2px;}"], _ref2 => {
  let {
    context,
    ghost,
    theme,
    isTransparent
  } = _ref2;
  return getColor(context, !!ghost, theme, 'mainColor', isTransparent);
}, _ref3 => {
  let {
    context,
    noBorder,
    theme
  } = _ref3;
  return noBorder ? 'none' : '1px solid ' + getColor(context, false, theme, 'mainColor');
}, _ref4 => {
  let {
    childrenLength,
    group
  } = _ref4;
  return childrenLength > 1 && group === 'default' ? '8px' : '0';
}, _ref5 => {
  let {
    context,
    ghost,
    theme,
    isTransparent
  } = _ref5;
  return getColor(context, !!ghost, theme, 'fontColor', isTransparent);
}, _ref6 => {
  let {
    ariaDisabled,
    context,
    theme
  } = _ref6;
  return !ariaDisabled && "linear-gradient(".concat(getColor(context, false, theme, 'shadeColor'), ", ").concat(getColor(context, false, theme, 'mainColor'), ")");
}, white, white, _ref7 => {
  let {
    context,
    theme
  } = _ref7;
  return getColor(context, false, theme, 'shadeColor');
}, _ref8 => {
  var _theme$fontColors, _theme$fontColors$bod;

  let {
    theme
  } = _ref8;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors = theme.fontColors) === null || _theme$fontColors === void 0 ? void 0 : (_theme$fontColors$bod = _theme$fontColors.body) === null || _theme$fontColors$bod === void 0 ? void 0 : _theme$fontColors$bod.disabled) || dustGray;
}, _ref9 => {
  let {
    theme
  } = _ref9;
  return (theme === null || theme === void 0 ? void 0 : theme.borderColor) || alto;
}, _ref10 => {
  let {
    block,
    group,
    width
  } = _ref10;
  return block ? '100%' : group === 'default' ? width ? width + 'px' : 'auto' : '20px';
}, _ref11 => {
  let {
    group
  } = _ref11;
  return group === 'default' ? '30px' : '20px';
}, _ref12 => {
  let {
    borderRadius,
    group
  } = _ref12;
  return group === 'circle' ? '15px' : (borderRadius || 3) + 'px';
}, _ref13 => {
  let {
    group
  } = _ref13;
  return group === 'default' ? '0 15px' : group === 'circle' ? '0' : '2px';
}, _ref14 => {
  let {
    group
  } = _ref14;
  return group === 'default' && '18px';
}, _ref15 => {
  let {
    block,
    group,
    width
  } = _ref15;
  return block ? '100%' : group === 'default' ? width ? width + 'px' : 'auto' : '30px';
}, _ref16 => {
  let {
    group
  } = _ref16;
  return group === 'default' ? '40px' : '30px';
}, _ref17 => {
  let {
    borderRadius,
    group
  } = _ref17;
  return group === 'circle' ? '20px' : (borderRadius || 4) + 'px';
}, _ref18 => {
  let {
    group
  } = _ref18;
  return group === 'default' ? '0 20px' : group === 'circle' ? '0' : '5px';
}, _ref19 => {
  let {
    group
  } = _ref19;
  return group === 'default' && '18px';
}, _ref20 => {
  let {
    block,
    group,
    width
  } = _ref20;
  return block ? '100%' : group === 'default' ? width ? width + 'px' : 'auto' : '40px';
}, _ref21 => {
  let {
    group
  } = _ref21;
  return group === 'default' ? '50px' : '40px';
}, _ref22 => {
  let {
    borderRadius,
    group
  } = _ref22;
  return group === 'circle' ? '25px' : (borderRadius || 5) + 'px';
}, _ref23 => {
  let {
    group
  } = _ref23;
  return group === 'default' ? '0 30px' : group === 'circle' ? '0' : '10px';
}, _ref24 => {
  let {
    group
  } = _ref24;
  return group === 'default' && '22px';
}, _ref25 => {
  let {
    block,
    group,
    width
  } = _ref25;
  return block ? '100%' : group === 'default' ? width ? width + 'px' : 'auto' : '50px';
}, _ref26 => {
  let {
    group
  } = _ref26;
  return group === 'default' ? '60px' : '50px';
}, _ref27 => {
  let {
    borderRadius,
    group
  } = _ref27;
  return group === 'circle' ? '30px' : (borderRadius || 6) + 'px';
}, _ref28 => {
  let {
    group
  } = _ref28;
  return group === 'default' ? '0 40px' : group === 'circle' ? '0' : '10px';
}, _ref29 => {
  let {
    group
  } = _ref29;
  return group === 'default' && '27px';
}, _ref30 => {
  var _theme$action;

  let {
    theme
  } = _ref30;
  return (theme === null || theme === void 0 ? void 0 : (_theme$action = theme.action) === null || _theme$action === void 0 ? void 0 : _theme$action.default) || java;
});
const ButtonContent = /*#__PURE__*/styled.span.withConfig({
  displayName: "Button__ButtonContent",
  componentId: "sc-1jofteb-1"
})(["display:flex;flex-direction:row;justify-content:center;align-items:center;"]);
const Pipe = /*#__PURE__*/styled.div.withConfig({
  displayName: "Button__Pipe",
  componentId: "sc-1jofteb-2"
})(["height:", ";margin-right:8px;border-right:2px solid ", ";"], _ref31 => {
  let {
    size
  } = _ref31;
  return size === 'xsmall' || size === 'small' ? '14px' : '20px';
}, _ref32 => {
  let {
    context,
    ghost,
    theme
  } = _ref32;
  return getColor(context, !!ghost, theme, 'fontColor');
});